import { Link } from "react-router-dom";
import { MessageCircle, ShieldCheck, ScanSearch, MapPin, ArrowRight, Wrench, Gauge, Settings2, Volume2, Car, Crosshair, Shield, CircleDollarSign, CalendarCheck, Clock, Check } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { IMG } from "@/lib/catalog";
import { waLink } from "@/lib/api";
import { FollowBar } from "@/components/SocialFeeds";
import { MapEmbed, DIRECTIONS_URL, WHATSAPP_MSG } from "@/components/Floating";
import { HoursList } from "@/components/Footer";
import { QuoteButton } from "@/components/QuoteDialog";
import { FaqSection, FeaturedReviews, LatestVideos } from "@/components/HomeExtras";

const WA = waLink("529991919300", "Hola, quiero agendar una revisión de suspensión.");

const Hero = () => (
  <section className="relative overflow-hidden bg-white" data-testid="home-hero">
    <div className="absolute inset-y-0 right-0 hidden w-[55%] lg:block">
      <img src={IMG.hero} alt="Mecánico revisando la suspensión de un vehículo" className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/30 to-transparent" />
    </div>
    <div className="container-x relative grid min-h-[640px] items-center py-16 lg:grid-cols-2">
      <div className="max-w-xl">
        <p className="eyebrow rise">Especialistas en suspensión, dirección y frenos · Mérida</p>
        <h1 className="rise mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02] text-ink" style={{ animationDelay: "80ms" }}>
          Conduce <span className="text-metal">más seguro</span> y sin ruidos
        </h1>
        <p className="rise mt-6 text-base md:text-lg text-ink2" style={{ animationDelay: "160ms" }}>Diagnóstico antes de cambiar piezas, explicación clara y un manejo más cómodo para ti y tu familia.</p>
        <ul className="rise mt-8 grid grid-cols-3 gap-4 text-xs font-semibold text-ink2" style={{ animationDelay: "220ms" }}>
          {[[ShieldCheck, "Más seguridad en cada viaje"], [Car, "Conducción más estable"], [Wrench, "Especialistas en suspensión"]].map(([I, t]) => (
            <li key={t} className="flex items-center gap-2"><I size={26} className="shrink-0 text-green" />{t}</li>
          ))}
        </ul>
        <div className="rise mt-10 flex flex-wrap gap-3" style={{ animationDelay: "280ms" }}>
          <a href={WA} target="_blank" rel="noopener noreferrer" className="btn-gold !px-7 !py-4 !text-base" data-testid="hero-whatsapp-btn"><MessageCircle size={20} /> Agenda tu revisión por WhatsApp <ArrowRight size={18} /></a>
          <Link to="/citas" className="btn-ghost !py-4" data-testid="hero-book-btn"><CalendarCheck size={18} /> Agendar en línea</Link>
        </div>
        <div className="mt-5 flex flex-wrap gap-5 text-xs text-mute">
          {["Atención rápida", "Cotización sin compromiso", "Asesoría personalizada"].map((t) => <span key={t} className="flex items-center gap-1.5"><Check size={14} className="text-green" />{t}</span>)}
        </div>
      </div>
      <img src={IMG.hero} alt="" aria-hidden="true" className="mt-10 h-64 w-full rounded-2xl object-cover lg:hidden" />
    </div>
  </section>
);

const TRUST = [[ScanSearch, "Diagnóstico preciso", "antes de cambiar piezas"], [CircleDollarSign, "Cotización clara", "sabes el costo antes del trabajo"], [CalendarCheck, "Citas en línea", "o por WhatsApp al 999-191-9300"], [MapPin, "Fácil de llegar", "Calle 136 #715-A, Mérida"]];

const TrustStrip = () => (
  <section className="border-y border-line bg-surface">
    <div className="container-x grid grid-cols-2 gap-6 py-8 lg:grid-cols-4">
      {TRUST.map(([I, t, d]) => (
        <div key={t} className="flex items-center gap-3"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-green shadow-sm"><I size={22} /></span>
          <div><p className="text-sm font-bold text-ink">{t}</p><p className="text-xs text-mute">{d}</p></div></div>
      ))}
    </div>
  </section>
);

const CARDS = [
  { t: "Suspensión", d: "Revisión y reparación de todo el sistema de suspensión.", img: IMG.chassis, icon: Wrench },
  { t: "Amortiguadores", d: "Diagnóstico, instalación y reemplazo.", img: IMG.shocks, icon: Gauge },
  { t: "Dirección", d: "Terminales, cremallera y componentes para un control preciso.", img: IMG.assembly, icon: Settings2 },
  { t: "Diagnóstico de ruidos", d: "Encontramos el origen de ruidos y vibraciones.", img: IMG.under, icon: Volume2 },
];

const ServiceCards = () => (
  <section className="container-x py-20" aria-labelledby="srv-title">
    <div className="grid gap-6 lg:grid-cols-2 lg:items-end">
      <div>
        <p className="eyebrow">Nuestros servicios</p>
        <h2 id="srv-title" className="mt-3 text-3xl sm:text-4xl font-extrabold">Todo lo que tu auto necesita en <span className="text-metal">un solo lugar</span></h2>
      </div>
      <p className="text-ink2">Nos especializamos en suspensión, dirección y frenos para que tu vehículo vuelva a sentirse como nuevo. También soldadura de chasis, lift spacers y cotización de refacciones.</p>
    </div>
    <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
      {CARDS.map((c, i) => (
        <Link to="/servicios" key={c.t} className="card-dark group overflow-hidden rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`home-service-${i}`}>
          <div className="h-44 overflow-hidden"><img src={c.img} alt={c.t} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
          <div className="flex items-end justify-between gap-3 p-5">
            <div><h3 className="font-display text-lg font-bold">{c.t}</h3><p className="mt-1 text-sm text-mute">{c.d}</p></div>
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line text-green transition-colors group-hover:bg-gold group-hover:text-ink"><ArrowRight size={16} /></span>
          </div>
        </Link>
      ))}
    </div>
    <div className="mt-8 flex flex-wrap gap-3">
      <Link to="/servicios" className="btn-ghost" data-testid="home-all-services-btn">Ver los 17 servicios <ArrowRight size={16} /></Link>
      <QuoteButton className="btn-gold" testId="home-quote-btn" />
    </div>
  </section>
);

const WHY = [[Car, "Más confort en cada trayecto"], [Crosshair, "Mejor control del vehículo"], [Shield, "Mayor seguridad para tu familia"], [Wrench, "Evita daños mayores en tu auto"]];

const Why = () => (
  <section className="container-x grid gap-10 py-12 lg:grid-cols-[1fr,2fr] lg:items-center">
    <div><p className="eyebrow">Tu seguridad es primero</p><h2 className="mt-3 text-2xl sm:text-3xl font-extrabold">¿Por qué es importante un buen sistema de suspensión?</h2></div>
    <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
      {WHY.map(([I, t]) => <div key={t} className="text-center"><I size={34} className="mx-auto text-brand" /><p className="mt-3 text-sm font-semibold text-ink2">{t}</p></div>)}
    </div>
  </section>
);

const STEPS = [
  { t: "Agendas tu revisión", d: "Nos cuentas qué le pasa a tu auto y agendamos tu visita.", img: IMG.bay },
  { t: "Revisamos y diagnosticamos", d: "Inspeccionamos suspensión, dirección y frenos a fondo.", img: IMG.fixing },
  { t: "Te damos la mejor solución", d: "Te explicamos el diagnóstico y las opciones recomendadas.", img: IMG.clipboard },
];

const Process = () => (
  <section className="container-x grid gap-10 py-16 lg:grid-cols-[1fr,3fr]">
    <div><p className="eyebrow">Nuestro proceso</p><h2 className="mt-3 text-2xl sm:text-3xl font-extrabold">Así de fácil es mejorar tu manejo</h2></div>
    <ol className="grid gap-6 sm:grid-cols-3">
      {STEPS.map((s, i) => (
        <li key={s.t}>
          <div className="relative h-36 overflow-hidden rounded-xl"><img src={s.img} alt="" loading="lazy" className="h-full w-full object-cover" />
            <span className="absolute left-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-gold font-display font-extrabold text-ink">{i + 1}</span></div>
          <h3 className="mt-4 font-display text-base font-bold">{s.t}</h3><p className="mt-1 text-sm text-mute">{s.d}</p>
        </li>
      ))}
    </ol>
  </section>
);

const CtaBand = () => (
  <section className="relative overflow-hidden bg-green2 text-white">
    <img src={IMG.road} alt="Camioneta circulando en carretera" className="absolute inset-0 h-full w-full object-cover opacity-50" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-r from-green2 via-green2/85 to-transparent" />
    <div className="container-x relative grid gap-8 py-20 lg:grid-cols-2">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-gold">Tu auto merece el mejor cuidado</p>
        <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold">Agenda tu revisión de suspensión hoy</h2>
        <p className="mt-3 text-white/80">Conduce con tranquilidad y en las mejores manos.</p>
        <a href={WA} target="_blank" rel="noopener noreferrer" className="btn-gold mt-8 !px-7 !py-4 !text-base" data-testid="cta-whatsapp-btn"><MessageCircle size={20} /> Agenda tu revisión por WhatsApp <ArrowRight size={18} /></a>
      </div>
      <ul className="space-y-4 self-center text-sm">
        {[[Clock, "Lunes a viernes 9:00 AM–5:00 PM · sábado 9:00 AM–3:00 PM"], [MapPin, "Calle 136 #715-A entre 133 y 135, Mérida"], [ShieldCheck, "Diagnóstico y cotización antes de reparar"]].map(([I, t]) => <li key={t} className="flex items-center gap-3"><I size={20} className="text-gold" />{t}</li>)}
      </ul>
    </div>
  </section>
);

const VisitUs = () => (
  <section className="container-x grid gap-10 py-20 lg:grid-cols-[1fr,1.4fr]" aria-labelledby="visit-title">
    <div>
      <p className="eyebrow">Visítanos</p>
      <h2 id="visit-title" className="mt-3 text-3xl font-extrabold">Calle 136 #715-A</h2>
      <p className="mt-2 text-ink2">Entre 133 y 135, Mérida, Yucatán 97306</p>
      <div className="mt-8 card-dark p-6"><HoursList /></div>
      <a href={DIRECTIONS_URL} target="_blank" rel="noopener noreferrer" className="btn-ghost mt-6" data-testid="home-contact-btn">Cómo llegar</a>
    </div>
    <MapEmbed />
  </section>
);

export default function Inicio() {
  useSeo("Taller de Suspensiones en Mérida | La Gringa — Los Héroes", "Especialistas en suspensión y dirección automotriz en Mérida. Más de 20 años de experiencia, diagnóstico visual gratis, precios justos y atención profesional. ¡Agenda tu cita!", true);
  return (
    <>
      <FollowBar />
      <Hero />
      <TrustStrip />
      <ServiceCards />
      <Why />
      <Process />
      <LatestVideos />
      <FeaturedReviews />
      <FaqSection />
      <CtaBand />
      <VisitUs />
    </>
  );
}

export { WHATSAPP_MSG };
